import { cn } from '@lib/util/cn';
import SectionCta from '@modules/common/components/section-cta';
import { Text } from '@modules/common/components/text';

type Cta = { label: string; href: string };

export type HeroContentProps = {
  overline: string;
  heading: string;
  headingSize?: 'medium' | 'large';
  headingId: string;
  intro: React.ReactNode;
  primaryCta?: Cta;
  secondaryCta?: Cta;
};

export default function HeroContent({
  overline,
  heading,
  headingSize = 'medium',
  headingId,
  intro,
  primaryCta,
  secondaryCta,
}: HeroContentProps) {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3">
        {overline ? (
          <Text className="nw-typography-eyebrow uppercase text-fg-neutral-strong">
            {overline}
          </Text>
        ) : null}
        <h1
          id={headingId}
          className={cn(
            'font-light text-fg-neutral-strong',
            headingSize === 'medium'
              ? 'nw-typography-heading-2xl'
              : 'nw-typography-heading-3xl',
          )}
        >
          {heading}
        </h1>
      </div>
      <Text className="nw-typography-paragraph text-pretty whitespace-pre-line text-fg-neutral-strong">
        {intro}
      </Text>
      <HeroCtas primaryCta={primaryCta} secondaryCta={secondaryCta} />
    </div>
  );
}

type CtaEntry = Cta & { variant: 'primary' | 'outlined' };

function HeroCtas({
  primaryCta,
  secondaryCta,
}: Pick<HeroContentProps, 'primaryCta' | 'secondaryCta'>) {
  const ctas: CtaEntry[] = [];
  if (primaryCta) ctas.push({ ...primaryCta, variant: 'primary' });
  if (secondaryCta) ctas.push({ ...secondaryCta, variant: 'outlined' });

  if (!ctas.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-3">
      {ctas.map((cta) => (
        <SectionCta
          key={cta.href}
          href={cta.href}
          label={cta.label}
          variant={cta.variant}
          size="md"
        />
      ))}
    </div>
  );
}
